


import React, { useState } from 'react';
import { Typography, TextField, Button, CircularProgress, Container, Grid, Paper, Box } from '@mui/material';
import { PieChart, Pie, Cell, Label } from 'recharts';

const StudentAttendance = ({ userId, rollNumber, schoolId }) => {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [showDatePickers, setShowDatePickers] = useState(true); // State to control showing date pickers
    const [isRecordsFetched, setIsRecordsFetched] = useState(false);
    const [showDetails, setShowDetails] = useState(false); // State to control showing attendance details

    // Function to fetch attendance records
    const fetchAttendanceRecords = async () => {
        setLoading(true);
        setError('');
        try {
            // Simulating API call using fetch
            const response = await fetch(`https://api.isnartecherp.online/api/v1/getAttendanceByUserId?userId=${userId}&rollNumber=${rollNumber}&schoolId=${schoolId}&fromDate=${fromDate}&toDate=${toDate}`);
            if (!response.ok) {
                throw new Error('Failed to fetch attendance records');
            }
            const data = await response.json();
            setAttendanceData(data.attendances);
            setIsRecordsFetched(true); // Records are fetched
            setShowDatePickers(false); // Hide date pickers after successful fetch
        } catch (error) {
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    // Function to format date to yyyy-mm-dd format
    const formatDate = (date) => {
        const [year, month, day] = date.split('-');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    };

    // Calculate total days in range and attendance stats
    const totalDaysInRange = fromDate && toDate ?
        Math.floor((new Date(toDate) - new Date(fromDate)) / (1000 * 60 * 60 * 24)) + 1 :
        0;
    const presentCount = attendanceData.filter(item => item.status === "Present").length;
    const absentCount = totalDaysInRange - presentCount;
    const attendancePercentage = totalDaysInRange > 0 ? ((presentCount / totalDaysInRange) * 100).toFixed(2) : 0;

    // Pie chart data
    const pieChartData = [
        { name: 'Present', value: presentCount },
        { name: 'Absent', value: absentCount }
    ];

    // Function to render each attendance record
    const renderAttendanceRecord = (record, index) => (
        <Paper
            elevation={3}
            key={index}
            style={{
                padding: '10px',
                marginBottom: '10px',
                boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.1)',
                border: '1px solid #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                backgroundColor: index % 2 === 0 ? '#f0f0f0' : '#ffffff' // Alternate row background color
            }}
        >
            <Typography variant="body1" style={{ marginRight: '20px' }}>{record.fullName}</Typography>
            <Typography variant="body1" style={{ marginRight: '20px' }}>{formatDate(new Date(record.date).toISOString().slice(0, 10))}</Typography>
            <Typography variant="body1">{record.status}</Typography>
        </Paper>
    );

    return (
        <Container maxWidth="md" style={{ padding: '20px', marginTop: '20px' }}>
            {!isRecordsFetched ? (
                // Render form to fetch records
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        {/* Header */}
                        <Box sx={{ width: '100%', backgroundColor: '#3f51b5', borderRadius: '8px' }}>
                            <Typography
                                variant="h5"
                                align="center"
                                gutterBottom
                                style={{
                                    fontWeight: 500,
                                    color: '#fff',
                                    padding: '8px 0'
                                }}
                            >
                                Check Your Attendance Records
                            </Typography>
                        </Box>
                        {/* Roll number field */}
                        <TextField
                            label="Roll Number"
                            value={rollNumber}
                            fullWidth
                            variant="outlined"
                            disabled
                            style={{ marginBottom: '20px' }}
                        />
                        {/* Date pickers */}
                        {showDatePickers ? (
                            <>
                                <TextField
                                    label="From Date"
                                    value={fromDate}
                                    onChange={(e) => setFromDate(e.target.value)}
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    style={{ marginBottom: '20px' }}
                                />
                                <TextField
                                    label="To Date"
                                    value={toDate}
                                    onChange={(e) => setToDate(e.target.value)}
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    style={{ marginBottom: '20px' }}
                                />
                            </>
                        ) : (
                            // Button to show date pickers
                            <Button
                                variant="contained"
                                onClick={() => setShowDatePickers(true)}
                                color="primary"
                                fullWidth
                                style={{ marginBottom: '20px', backgroundColor: '#3f51b5', color: '#ffffff' }}
                            >
                                Change Date Range
                            </Button>
                        )}
                        {/* Button to fetch records */}
                        <div style={{ display: 'flex', justifyContent: 'center' }}>
                            <Button
                                variant="contained"
                                onClick={fetchAttendanceRecords}
                                color="primary"
                                style={{
                                    width: 'fit-content',  // Adjusts width to fit content
                                    marginBottom: '20px',
                                    backgroundColor: '#3f51b5',
                                    color: '#ffffff',
                                    textTransform: 'none'  // Prevents uppercase transformation
                                }}
                            >
                                Fetch Records
                            </Button>
                        </div>
                        {/* Error message */}
                        {error && <Typography variant="body1" color="error" align="center">{error}</Typography>}
                    </Grid>
                </Grid>
            ) : (
                // Render attendance records after records are fetched
                <Grid container spacing={2} justifyContent="center">
                    <Grid item xs={12}>
                        {/* Button to change date range */}
                        <Button
                            variant="contained"
                            onClick={() => setIsRecordsFetched(false)}
                            color="primary"
                            fullWidth
                            style={{ marginBottom: '20px', backgroundColor: '#3f51b5', color: '#ffffff' }}
                        >
                            Check For Other Date Range
                        </Button>
                        {/* Date range information */}
                        <Typography variant="h6" align="center" gutterBottom>Showing attendance for {formatDate(fromDate)} to {formatDate(toDate)}</Typography>
                        {/* Loading spinner */}
                        {loading ? (
                            <Grid container justifyContent="center">
                                <Grid item xs={12} style={{ marginTop: '20px' }}>
                                    <CircularProgress />
                                </Grid>
                            </Grid>
                        ) : (
                            <Grid container spacing={2} justifyContent="center">
                                <Grid item xs={12}>
                                    {/* Attendance summary */}
                                    <Typography variant="h5" align="center" gutterBottom>Total Attendance: {presentCount}</Typography>
                                    <Typography variant="h5" align="center" gutterBottom>Attendance Percentage: {attendancePercentage}%</Typography>
                                    {/* Pie chart */}
                                    <Box display="flex" justifyContent="center" mt={2}>
                                        <PieChart width={400} height={300}>
                                            <Pie
                                                dataKey="value"
                                                data={pieChartData}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                label
                                            >
                                                {pieChartData.map((entry, index) => (
                                                    <Cell key={`cell-${index}`} fill={['#36A2EB', '#FF6384'][index % 2]} />
                                                ))}
                                                <Label
                                                    value="Attendance"
                                                    position="center"
                                                    style={{ fontSize: '20px' }}
                                                />
                                            </Pie>
                                        </PieChart>
                                    </Box>
                                    <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
                                        <Button
                                            variant="contained"
                                            onClick={() => setShowDetails(!showDetails)}
                                            color="primary"
                                            style={{
                                                width: 'fit-content',
                                                backgroundColor: '#3f51b5',
                                                color: '#ffffff',
                                                textTransform: 'none',
                                                marginBottom:10
                                            }}
                                        >
                                            {showDetails ? 'Hide Details' : 'Show Details'}
                                        </Button>
                                    </div>
                                    {showDetails && attendanceData.map((record, index) => (
                                        renderAttendanceRecord(record, index)
                                    ))}
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            )}
        </Container>
    );
};

export default StudentAttendance;
